import React from 'react';
import { motion } from 'framer-motion';
import { Cake, Flower2, Sun, Leaf } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ZoomableImage } from './ZoomableImage';

export const MenuCardDesserts = ({ dishes }) => {
  const { addToCart } = useCart();

  const handleAddToCart = async (dish) => {
    try {
      await addToCart(dish.dish_id, 1);
      toast.success(`${dish.name} ajouté au panier`);
    } catch (error) {
      toast.error('Connectez-vous pour commander');
    }
  };

  // Group dishes by mois
  const mois1 = dishes.filter(d => d.mois === 'mois_1');
  const mois2 = dishes.filter(d => d.mois === 'mois_2');
  const mois3 = dishes.filter(d => d.mois === 'mois_3');

  const MenuItem = ({ dish, index }) => (
    <motion.div
      key={dish.dish_id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <div 
        className="py-6 px-6 -mx-6 rounded-2xl hover:bg-[#EDE4D4] transition-all cursor-pointer"
        onClick={() => handleAddToCart(dish)}
        data-testid={`dessert-item-${dish.dish_id}`}
      >
        {dish.image_url && (
          <div className="mb-4 rounded-2xl overflow-hidden max-w-md mx-auto shadow-md">
            <ZoomableImage
              src={dish.image_url}
              alt={dish.name}
              className="w-full h-auto aspect-[16/9] object-cover"
              loading="lazy"
            />
          </div>
        )}
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-6">
            <h4 className="font-heading text-xl font-semibold text-[#3E2723] group-hover:text-[#8B5A2B] transition-colors">
              {dish.name}
            </h4>
            <p className="text-[#6D4C41] text-sm mt-2 leading-relaxed">
              {dish.description}
            </p>
            {dish.ingredients && dish.ingredients.length > 0 && (
              <p className="text-[#8B7355]/70 text-xs mt-3 italic">
                {dish.ingredients.join(' • ')}
              </p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xs text-[#8B5A2B] mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              + Ajouter
            </p>
          </div>
        </div>
      </div>
      <div className="border-b border-[#D4C4AA]/40"></div>
    </motion.div>
  );

  const MoisSection = ({ items, moisNumber, moisTitle, icon: Icon, iconColor }) => {
    if (items.length === 0) return null;
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${iconColor}`}>
            <Icon size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-[#8B7355] uppercase tracking-wider">
              Mois {moisNumber}
            </p>
            <h3 className="font-heading text-xl font-bold text-[#3E2723]">
              {moisTitle}
            </h3>
          </div>
        </div>
        <div className="bg-white/60 rounded-2xl p-6 border border-[#D4C4AA]/30">
          {items.map((dish, index) => (
            <MenuItem key={dish.dish_id} dish={dish} index={index} />
          ))}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Elegant Header */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-block"
        >
          {/* Decorative element */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-20 h-[1px] bg-gradient-to-r from-transparent to-[#8B5A2B]"></span>
            <Cake className="text-[#8B5A2B]" size={32} />
            <span className="w-20 h-[1px] bg-gradient-to-l from-transparent to-[#8B5A2B]"></span>
          </div>
          
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#3E2723] tracking-wide">
            Desserts de Haute Pâtisserie
          </h2>
          <p className="text-[#8B5A2B] mt-2 text-lg italic font-heading">
            La Trilogie Sucrée Prestige
          </p>
          <p className="text-[#6D4C41] mt-4 max-w-md mx-auto text-sm">
            Une collection exclusive de créations sucrées, évoluant au fil des saisons
          </p>
        </motion.div>
      </div>

      {/* Menu Card Container */}
      <div className="bg-[#FAF8F4] rounded-3xl p-8 md:p-10 shadow-card border border-[#D4C4AA]/30 relative">
        {/* Elegant border decoration */}
        <div className="absolute inset-4 border border-[#D4C4AA]/20 rounded-2xl pointer-events-none"></div>
        
        <div className="relative z-10">
          {/* Mois I */}
          <MoisSection 
            items={mois1} 
            moisNumber="I" 
            moisTitle={mois1[0]?.mois_label || "Harmonies Printanières"}
            icon={Flower2}
            iconColor="bg-pink-100 text-pink-500"
          />

          {/* Mois II - Contrastes Gourmands */}
          <MoisSection 
            items={mois2} 
            moisNumber="II" 
            moisTitle={mois2[0]?.mois_label || "Contrastes Gourmands"}
            icon={Sun}
            iconColor="bg-amber-100 text-amber-500"
          />

          {/* Mois III */}
          <MoisSection 
            items={mois3} 
            moisNumber="III" 
            moisTitle={mois3[0]?.mois_label || "Douceurs Automnales"}
            icon={Leaf}
            iconColor="bg-orange-100 text-orange-600"
          />
        </div>
      </div>

      {/* Footer note */}
      <div className="text-center mt-8">
        <p className="text-[#6D4C41]/70 text-sm italic">
          Cliquez sur un dessert pour l'ajouter au panier
        </p>
        <p className="text-[#6D4C41]/50 text-xs mt-2">
          Créations élaborées par notre Chef Pâtissier • Sur commande 48h à l'avance
        </p>
      </div>
    </div>
  );
};
